import moment from "moment-timezone";
import { getAllCountries, getCountryForTimezone, getTimezonesForCountry } from 'countries-and-timezones';

interface TimezoneWithCountry {
  value?: string;
  countryName: string;
  timezone: string;
  utcOffset: string;
  name?: string;
  country?: string;
  offset?: string;
}

// Safe moment timezone wrapper to handle undefined errors
const safeMomentTz = (timezone?: string) => {
  try {
    if (timezone && moment.tz.zone(timezone)) {
      return moment.tz(timezone);
    }
    return moment();
  } catch (error) {
    console.warn('[Moment] Timezone error, falling back to local:', error);
    return moment();
  }
};

// Safe timezone names getter
const getSafeTimezoneNames = (): string[] => {
  try {
    return moment.tz.names() || [];
  } catch (error) {
    console.warn('[Moment] Could not load timezone names:', error);
    return [
      'UTC',
      'America/New_York',
      'America/Los_Angeles',
      'Europe/London',
      'Europe/Paris',
      'Asia/Tokyo',
      'Asia/Kolkata',
      'Australia/Sydney'
    ];
  }
};

// Enhanced UTC detection function
const isUTCTime = (dateString: string, timeSlot: string): boolean => {
  try {
    // Check if the date string contains UTC indicators
    if (dateString.includes('Z') || dateString.includes('UTC') || dateString.includes('GMT')) {
      return true;
    }
    
    // Check if timezone is explicitly UTC
    if (timeSlot && (timeSlot.includes('UTC') || timeSlot.includes('GMT'))) {
      return true;
    }
    
    // Check if the date string is in ISO format without timezone (assumed to be UTC)
    // This handles cases like "2025-07-25T06:30:00" or "2025-07-29T05:45:36.822" which are UTC times from API
    if (dateString && dateString.match(/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/)) {
      return true;
    }
    
    return false;
  } catch (error) {
    console.warn('[Moment] Error detecting UTC time:', error);
    return false;
  }
};

/** Parses API datetime strings (numeric offset, UTC / ISO without Z, or ambiguous local) into a zone. Used by chat/timeline formatters only. */
const parseApiDateTimeToUserMoment = (
  dateString: string,
  targetTimezone: string
): moment.Moment | null => {
  try {
    if (!dateString || typeof dateString !== 'string') return null;
    const t = dateString.trim();
    const hasNumericOffset = /[+-]\d{2}:?\d{2}(:?\d{2})?$/.test(t);

    let m: moment.Moment;
    if (hasNumericOffset) {
      m = moment(t).tz(targetTimezone);
    } else if (isUTCTime(t, '')) {
      m = moment.utc(t).tz(targetTimezone);
    } else {
      m = moment.tz(t, targetTimezone);
    }
    if (!m.isValid()) return null;
    return m;
  } catch (error) {
    console.warn('[Moment] parseApiDateTimeToUserMoment:', error);
    return null;
  }
};

// Enhanced UTC to timezone conversion
const convertUTCToTimezone = (
  utcDateString: string,
  utcTimeSlot: string,
  targetTimezone: string
): { convertedDate: string; convertedTimeSlot: string; displayTimeSlot: string } => {
  try {
    // Clean UTC date string (remove Z, UTC, GMT indicators)
    let cleanDateString = utcDateString
      .replace(/Z$/, '')
      .replace(/UTC$/, '')
      .replace(/GMT$/, '')
      .trim();
    
    // Parse the time slot
    const [startTime, endTime] = utcTimeSlot.split(' - ');
    if (!startTime || !endTime) {
      return {
        convertedDate: utcDateString,
        convertedTimeSlot: utcTimeSlot,
        displayTimeSlot: utcTimeSlot
      };
    }
    
    // Create UTC moment objects
    const dateStr = cleanDateString.split('T')[0]; // Get just the date part
    const utcStartDateTime = moment.utc(`${dateStr}T${startTime}:00`);
    const utcEndDateTime = moment.utc(`${dateStr}T${endTime}:00`);
    
    // Convert to target timezone
    const convertedStart = utcStartDateTime.clone().tz(targetTimezone);
    const convertedEnd = utcEndDateTime.clone().tz(targetTimezone);
    
    // Handle case where end time appears to be before start time after conversion
    // This can happen when crossing date boundaries
    let finalEnd = convertedEnd;
    if (convertedEnd.isBefore(convertedStart)) {
      // If end time is before start time, it means we crossed midnight
      // The end time should be on the same date as the start time
      const startDate = convertedStart.format('YYYY-MM-DD');
      const endTimeOnly = convertedEnd.format('HH:mm:ss');
      finalEnd = moment.tz(`${startDate}T${endTimeOnly}`, targetTimezone);
    }
    
    // Format the converted date and time slot
    const convertedDate = convertedStart.format('YYYY-MM-DDTHH:mm:ss');
    const convertedTimeSlot = `${convertedStart.format('HH:mm')} - ${finalEnd.format('HH:mm')}`;
    
    // Format for display (12-hour format)
    const displayTimeSlot = `${convertedStart.format('hh:mm a')} - ${finalEnd.format('hh:mm a')}`;
    
    return { convertedDate, convertedTimeSlot, displayTimeSlot };
  } catch (error) {
    console.error('[Moment] Error converting UTC to timezone:', error);
    return {
      convertedDate: utcDateString,
      convertedTimeSlot: utcTimeSlot,
      displayTimeSlot: utcTimeSlot
    };
  }
};

export function getTimezonesWithCountry(): TimezoneWithCountry[] {
  try {
    const timezoneNames = getSafeTimezoneNames();
    const timezonesWithCountry = [];
    
    timezoneNames.forEach((name) => {
      try {
        const country: any = getCountryForTimezone(name);
        const offset = safeMomentTz(name).format("Z");
        
        if (country?.name) {
          timezonesWithCountry.push({
            value: name,
            timezone: name,
            country: country?.name,
            offset: offset,
          });
        }
      } catch (error) {
        console.warn(`[Moment] Error processing timezone ${name}:`, error);
      }
    });

    let timeZoneList = [];

    timezonesWithCountry.forEach((element) => {
      try {
        timeZoneList.push({
          value: element.value,
          countryName: element.country,
          name:
            "(UTC" +
            element.offset +
            ") " +
            element.timezone.replace(/.*\//, element.country + "/"),
        });
      } catch (error) {
        console.warn('[Moment] Error formatting timezone:', error);
      }
    });

    // Safe sort by offset
    timeZoneList.sort((a, b) => {
      try {
        const offsetToMinutes = (offset: string) => {
          try {
            const match = offset.match(/\(UTC([+-]\d{2}:\d{2})\)/);
            if (!match || !match[1]) return 0;
            
            const sign = match[1][0] === "-" ? -1 : 1;
            const [hours, minutes] = match[1].slice(1).split(":").map(Number);
            return sign * (hours * 60 + (minutes || 0));
          } catch {
            return 0;
          }
        };

        const aOffset = offsetToMinutes(a.name || '');
        const bOffset = offsetToMinutes(b.name || '');
        return aOffset - bOffset;
      } catch (error) {
        console.warn('[Moment] Error sorting timezones:', error);
        return 0;
      }
    });

    return timeZoneList;
  } catch (error) {
    console.error('[Moment] Critical error in getTimezonesWithCountry:', error);
    // Return minimal fallback
    return [
      {
        value: 'UTC',
        countryName: 'UTC',
        name: '(UTC+00:00) UTC',
        timezone: 'UTC',
        utcOffset: '+00:00'
      }
    ];
  }
}

export function getTimezonesWithCountryName(countryName: string): TimezoneWithCountry[] {
  try {
    if (!countryName) {
      return [];
    }

    const timezoneNames = getSafeTimezoneNames();
    const timezonesWithCountry = [];
    
    timezoneNames.forEach((name) => {
      try {
        const country: any = getCountryForTimezone(name);
        const offset = safeMomentTz(name).format("Z");
        
        if (country?.name && countryName.toLowerCase() === country?.name?.toLowerCase()) {
          timezonesWithCountry.push({
            value: name,
            timezone: name,
            country: country?.name,
            offset: offset,
          });
        }
      } catch (error) {
        console.warn(`[Moment] Error processing timezone ${name} for country ${countryName}:`, error);
      }
    });

    let timeZoneList = [];

    timezonesWithCountry.forEach((element) => {
      try {
        timeZoneList.push({
          value: element.value,
          name:
            "(UTC" +
            element.offset +
            ") " +
            element.timezone.replace(/.*\//, element.country + "/"),
        });
      } catch (error) {
        console.warn('[Moment] Error formatting timezone for country:', error);
      }
    });

    // Safe sort by offset
    timeZoneList.sort((a, b) => {
      try {
        const offsetToMinutes = (offset: string) => {
          try {
            const match = offset.match(/\(UTC([+-]\d{2}:\d{2})\)/);
            if (!match || !match[1]) return 0;
            
            const sign = match[1][0] === "-" ? -1 : 1;
            const [hours, minutes] = match[1].slice(1).split(":").map(Number);
            return sign * (hours * 60 + (minutes || 0));
          } catch {
            return 0;
          }
        };

        const aOffset = offsetToMinutes(a.name || '');
        const bOffset = offsetToMinutes(b.name || '');
        return aOffset - bOffset;
      } catch (error) {
        console.warn('[Moment] Error sorting timezones:', error);
        return 0;
      }
    });

    return timeZoneList;
  } catch (error) {
    console.error('[Moment] Critical error in getTimezonesWithCountryName:', error);
    return [];
  }
}

// Export safe moment wrapper for use in other components
export { safeMomentTz };

// Utility function to format timezone display name using moment.js
export const formatTimezoneDisplay = (timezone: string): string => {
  try {
    if (!timezone) return '';
    
    const momentTz = moment.tz(timezone);
    const offset = momentTz.format('Z');
    const timezoneName = timezone.split('/').pop()?.replace('_', ' ') || timezone;
    
    return `(UTC${offset}) ${timezoneName}`;
  } catch (error) {
    console.warn('[Moment] Error formatting timezone display:', error);
    return timezone;
  }
};

// Additional utility function to get current time in a specific timezone
export const getCurrentTimeInTimezone = (timezone: string): string => {
  try {
    if (!timezone) return '';
    return moment.tz(timezone).format('YYYY-MM-DD HH:mm:ss Z');
  } catch (error) {
    console.warn('[Moment] Error getting current time in timezone:', error);
    return '';
  }
};

// Utility function to check if a timezone is valid
export const isValidTimezone = (timezone: string): boolean => {
  try {
    return moment.tz.zone(timezone) !== null;
  } catch (error) {
    return false;
  }
};

// Utility function to get timezone offset in hours
export const getTimezoneOffsetHours = (timezone: string): number => {
  try {
    if (!timezone) return 0;
    const momentTz = moment.tz(timezone);
    return momentTz.utcOffset() / 60; // Convert minutes to hours
  } catch (error) {
    console.warn('[Moment] Error getting timezone offset:', error);
    return 0;
  }
};

// Utility function to get user timezone with fallback chain
export const getUserTimezone = (reduxState?: any): string => {
  try {
    // Priority: Redux (from GetBasicProfile API) > sessionStorage > localStorage > browser default
    const reduxTimezone = reduxState?.UserIdentity?.[0]?.userTimezone;
    const sessionTimezone = typeof window !== 'undefined' && JSON.parse(sessionStorage.getItem('userIdentity') || '{}')?.userTimezone;
    const localTimezone = typeof window !== 'undefined' && localStorage.getItem('userTimezone');
    const browserTimezone = moment.tz.guess();
    
    const finalTimezone = reduxTimezone || sessionTimezone || localTimezone || browserTimezone || 'Asia/Calcutta';
    
    // Validate the timezone
    if (!isValidTimezone(finalTimezone)) {
      console.warn('[Moment] Invalid timezone detected, falling back to browser timezone:', finalTimezone);
      return browserTimezone || 'Asia/Calcutta';
    }
    
    return finalTimezone;
  } catch (error) {
    console.warn('[Moment] Error getting user timezone:', error);
    return 'Asia/Calcutta';
  }
};

// Enhanced booking time conversion with better error handling and UTC support
export const convertBookingTimeWithUserTimezone = (
  bookingDate: string,
  bookingTimeSlot: string,
  bookingTimezone: string,
  userTimezone?: string,
  reduxState?: any
): { convertedDate: string; convertedTimeSlot: string; displayTimeSlot: string } => {
  try {
    // Get user timezone if not provided
    const targetTimezone = userTimezone || getUserTimezone(reduxState);
    
    // Check if booking time is in UTC
    if (isUTCTime(bookingDate, bookingTimeSlot)) {
      return convertUTCToTimezone(bookingDate, bookingTimeSlot, targetTimezone);
    }
    
    // Validate timezones
    if (!isValidTimezone(bookingTimezone)) {
      console.warn('[Moment] Invalid booking timezone:', bookingTimezone);
      return {
        convertedDate: bookingDate,
        convertedTimeSlot: bookingTimeSlot,
        displayTimeSlot: bookingTimeSlot
      };
    }
    
    if (!isValidTimezone(targetTimezone)) {
      console.warn('[Moment] Invalid user timezone:', targetTimezone);
      return {
        convertedDate: bookingDate,
        convertedTimeSlot: bookingTimeSlot,
        displayTimeSlot: bookingTimeSlot
      };
    }
    
    // Parse the booking date and time slot
    const [startTime, endTime] = bookingTimeSlot.split(' - ');
    if (!startTime || !endTime) {
      return {
        convertedDate: bookingDate,
        convertedTimeSlot: bookingTimeSlot,
        displayTimeSlot: bookingTimeSlot
      };
    }
    
    // Create moment objects in the source timezone
    const dateStr = bookingDate.split('T')[0]; // Get just the date part
    const startDateTime = moment.tz(`${dateStr}T${startTime}:00`, bookingTimezone);
    const endDateTime = moment.tz(`${dateStr}T${endTime}:00`, bookingTimezone);
    
    // Convert to target timezone
    const convertedStart = startDateTime.clone().tz(targetTimezone);
    const convertedEnd = endDateTime.clone().tz(targetTimezone);
    
    // Handle case where end time appears to be before start time after conversion
    // This can happen when crossing date boundaries
    let finalEnd = convertedEnd;
    if (convertedEnd.isBefore(convertedStart)) {
      // If end time is before start time, it means we crossed midnight
      // The end time should be on the same date as the start time
      const startDate = convertedStart.format('YYYY-MM-DD');
      const endTimeOnly = convertedEnd.format('HH:mm:ss');
      finalEnd = moment.tz(`${startDate}T${endTimeOnly}`, targetTimezone);
    }
    
    // Format the converted date and time slot
    const convertedDate = convertedStart.format('YYYY-MM-DDTHH:mm:ss');
    const convertedTimeSlot = `${convertedStart.format('HH:mm')} - ${finalEnd.format('HH:mm')}`;
    
    // Format for display (12-hour format) - use the converted moment objects directly
    const displayTimeSlot = `${convertedStart.format('hh:mm a')} - ${finalEnd.format('hh:mm a')}`;
    
    return {
      convertedDate,
      convertedTimeSlot,
      displayTimeSlot
    };
  } catch (error) {
    console.error('[Moment] Error in convertBookingTimeWithUserTimezone:', error);
    return {
      convertedDate: bookingDate,
      convertedTimeSlot: bookingTimeSlot,
      displayTimeSlot: bookingTimeSlot
    };
  }
};

/** Convert booking time from one IANA timezone to another; reuses {@link convertBookingTimeWithUserTimezone}. */
export const convertBookingTimeToTimezone = (
  bookingDate: string,
  bookingTimeSlot: string,
  fromTimezone: string,
  toTimezone: string
): { convertedDate: string; convertedTimeSlot: string } => {
  if (!bookingDate || !bookingTimeSlot || !fromTimezone || !toTimezone) {
    return { convertedDate: bookingDate, convertedTimeSlot: bookingTimeSlot };
  }
  const result = convertBookingTimeWithUserTimezone(
    bookingDate,
    bookingTimeSlot,
    fromTimezone,
    toTimezone,
    undefined
  );
  return { convertedDate: result.convertedDate, convertedTimeSlot: result.convertedTimeSlot };
};

// New function specifically for converting UTC times to any timezone
export const convertUTCToUserTimezone = (
  utcDateString: string,
  utcTimeSlot: string,
  userTimezone?: string,
  reduxState?: any
): { convertedDate: string; convertedTimeSlot: string; displayTimeSlot: string } => {
  try {
    const targetTimezone = userTimezone || getUserTimezone(reduxState);
    return convertUTCToTimezone(utcDateString, utcTimeSlot, targetTimezone);
  } catch (error) {
    console.error('[Moment] Error in convertUTCToUserTimezone:', error);
    return {
      convertedDate: utcDateString,
      convertedTimeSlot: utcTimeSlot,
      displayTimeSlot: utcTimeSlot
    };
  }
};

// New function specifically for converting UTC chat timestamps to user timezone
export const convertChatTimeToUserTimezone = (
  utcTimestamp: string,
  userTimezone?: string,
  reduxState?: any,
  fullDateTimeFormat = 'MMM D, YYYY hh:mm a'
): { 
  displayTime: string; 
  displayDate: string; 
  fullDateTime: string;
  isToday: boolean;
  isYesterday: boolean;
} => {
  try {
    const targetTimezone = userTimezone || getUserTimezone(reduxState);
    
    if (!utcTimestamp) {
      return {
        displayTime: '',
        displayDate: '',
        fullDateTime: '',
        isToday: false,
        isYesterday: false
      };
    }

    const userMoment = parseApiDateTimeToUserMoment(utcTimestamp, targetTimezone);
    if (!userMoment) {
      return {
        displayTime: '',
        displayDate: '',
        fullDateTime: '',
        isToday: false,
        isYesterday: false
      };
    }

    const now = moment().tz(targetTimezone);
    const today = now.clone().startOf('day');
    const yesterday = today.clone().subtract(1, 'day');
    const messageDate = userMoment.clone().startOf('day');
    
    const isToday = messageDate.isSame(today);
    const isYesterday = messageDate.isSame(yesterday);
    
    // Format time (12-hour format)
    const displayTime = userMoment.format('hh:mm a');
    
    // Format date based on when the message was sent
    let displayDate: string;
    if (isToday) {
      displayDate = 'Today';
    } else if (isYesterday) {
      displayDate = 'Yesterday';
    } else {
      displayDate = userMoment.format('MMM D, YYYY');
    }
    
    // Full date time for tooltips or detailed display (optional format, e.g. project timeline)
    const fullDateTime = userMoment.format(fullDateTimeFormat);
    
    return {
      displayTime,
      displayDate,
      fullDateTime,
      isToday,
      isYesterday
    };
  } catch (error) {
    console.error('[Moment] Error in convertChatTimeToUserTimezone:', error);
    return {
      displayTime: '',
      displayDate: '',
      fullDateTime: '',
      isToday: false,
      isYesterday: false
    };
  }
};

// Function to format chat message time for list display (last message time)
export const formatChatListTime = (
  utcTimestamp: string,
  userTimezone?: string,
  reduxState?: any,
  weekdayFormat: 'long' | 'short' = 'short'
): string => {
  try {
    const targetTimezone = userTimezone || getUserTimezone(reduxState);
    
    if (!utcTimestamp) {
      return '';
    }

    const userMoment = parseApiDateTimeToUserMoment(utcTimestamp, targetTimezone);
    if (!userMoment) {
      return '';
    }
    const now = moment().tz(targetTimezone);
    
    const diffInHours = now.diff(userMoment, 'hours');
    const diffInDays = now.diff(userMoment, 'days');
    
    if (diffInHours < 24) {
      // Today - show time
      return userMoment.format('hh:mm a');
    } else if (diffInDays < 7) {
      // Within a week - show day name
      return userMoment.format(weekdayFormat === 'short' ? 'ddd' : 'dddd');
    } else {
      // Older - show date
      return userMoment.format('MMM D');
    }
  } catch (error) {
    console.error('[Moment] Error in formatChatListTime:', error);
    return '';
  }
};
